import { GameObjects, Math as PhaserMath, Scene } from 'phaser';
import type { FinishPlayAttemptInput, TurboDuelQuestionResult } from '../../../../shared/results';
import {
  EventBus,
  RACE_GAME_FINISHED_EVENT,
  RACE_HUD_UPDATE_EVENT,
  type RaceGameResult,
  type RaceHudUpdate,
} from '../../../game/EventBus';
import { buildLearningReview } from '../../../game/domain/learningReview';
import { createLearningResultOverlay, RESULT_OVERLAY_DEPTH } from '../../../game/ui/LearningResultOverlay';
import type { TurboDuelChoice, TurboDuelConfig, TurboVehicleColor } from '../domain/raceLevels';
import {
  advanceRaceMotion,
  applyRaceMotionOutcome,
  initialRaceMotionState,
  raceMotionPlacement,
  racerSpeed,
  RACE_BOOST_DURATION_MS,
  RACE_CRUISE_SPEED,
  type RaceMotionState,
} from '../domain/raceMotion';
import { projectRoadPoint, roadCenterX, roadHalfWidth } from '../domain/roadProjection';
import { applyRaceAnswer, initialRaceRuleState, type RaceAnswerOutcome, type RaceRuleState } from '../domain/raceRules';
import { sectorForProgress } from '../domain/trackProgress';
import { RaceAudio } from './RaceAudio';
import { RaceVehicleRig } from './RaceVehicleRig';

const WIDTH = 1280;
const HEIGHT = 720;
const CAR_FRAME = 724;
const CAR_TEXTURE = 'turbo-duel-cars';
const RACE_CAR_TEXTURE = 'turbo-duel-race-rear-cars';
const colors: TurboVehicleColor[] = ['coral', 'azure', 'lime'];

type RacePhase = 'vehicle-select' | 'countdown' | 'racing' | 'feedback' | 'finish';
type AnswerActor = { container: GameObjects.Container; choice: TurboDuelChoice; index: number };

export class TurboDuelScene extends Scene {
  private readonly config: TurboDuelConfig;
  private readonly gameId: string;
  private readonly restartEnabled: boolean;
  private readonly audio = new RaceAudio();
  private readonly mediaTextureKeys = new Map<string, string>();
  private phase: RacePhase = 'vehicle-select';
  private ruleState: RaceRuleState = initialRaceRuleState();
  private motionState: RaceMotionState = initialRaceMotionState();
  private questionIndex = 0;
  private selectedColor: TurboVehicleColor = 'coral';
  private questionElapsedMs = 0;
  private remainingMs: number | null = null;
  private cameraDistance = 1_000;
  private answerActors: AnswerActor[] = [];
  private promptObjects: GameObjects.GameObject[] = [];
  private overlayObjects: GameObjects.GameObject[] = [];
  private questionResults: TurboDuelQuestionResult[] = [];
  private world!: GameObjects.Graphics;
  private playerCar?: RaceVehicleRig;
  private botCars: RaceVehicleRig[] = [];
  private lanePulse?: { outcome: RaceAnswerOutcome; elapsedMs: number; durationMs: number; direction: -1 | 1 };
  private speedLines?: GameObjects.Graphics;
  private feedbackText?: GameObjects.Text;
  private resultEmitted = false;

  constructor(config: TurboDuelConfig, gameId: string, restartEnabled = true) {
    super('TurboDuel');
    this.config = config;
    this.gameId = gameId;
    this.restartEnabled = restartEnabled;
  }

  preload() {
    if (!this.textures.exists(CAR_TEXTURE)) {
      this.load.spritesheet(CAR_TEXTURE, '/odevlendir/assets/turbo-duel/electric-gt-cars.png', {
        frameWidth: CAR_FRAME, frameHeight: CAR_FRAME,
      });
    }
    if (!this.textures.exists(RACE_CAR_TEXTURE)) {
      this.load.spritesheet(RACE_CAR_TEXTURE, '/odevlendir/assets/turbo-duel/electric-gt-race-rear.png', {
        frameWidth: CAR_FRAME, frameHeight: CAR_FRAME,
      });
    }
    const urls = new Set<string>();
    this.config.questions.forEach((question) => {
      if (question.promptMediaUrl) urls.add(question.promptMediaUrl);
      question.choices.forEach((choice) => { if (choice.mediaUrl) urls.add(choice.mediaUrl); });
    });
    [...urls].forEach((url, index) => {
      const key = `${this.gameId}-race-media-${index}`;
      this.mediaTextureKeys.set(url, key);
      if (!this.textures.exists(key)) this.load.image(key, url);
    });
  }

  create() {
    this.ruleState = initialRaceRuleState();
    this.motionState = initialRaceMotionState();
    this.cameraDistance = this.motionState.racers.player.distance;
    this.phase = 'vehicle-select';
    this.questionIndex = 0;
    this.questionResults = [];
    this.lanePulse = undefined;
    this.resultEmitted = false;
    this.world = this.add.graphics().setDepth(0);
    this.speedLines = this.add.graphics().setDepth(20);
    this.drawWorld();
    this.input.on('pointerdown', () => this.audio.unlock());
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      this.audio.unlock();
      const index = Number(event.key) - 1;
      if (this.phase === 'racing' && index >= 0 && index < this.answerActors.length) this.choose(index);
    });
    this.installDebugHooks();
    this.createMuteControl();
    this.showVehiclePicker();
    this.refreshHud();
    this.events.once('shutdown', () => this.audio.destroy());
    EventBus.emit('current-scene-ready', this);
  }

  private createMuteControl() {
    const button = this.add.rectangle(1182, 325, 145, 42, 0x09243a, 0.88).setStrokeStyle(2, 0x67dfea)
      .setInteractive({ useHandCursor: true }).setDepth(65).setScrollFactor(0);
    const label = this.add.text(1182, 325, '🔊 Ses açık', {
      fontFamily: 'system-ui, sans-serif', fontSize: '16px', fontStyle: 'bold', color: '#e8fbff',
    }).setOrigin(0.5).setDepth(66).setScrollFactor(0);
    const toggle = () => label.setText(this.audio.toggleMuted() ? '🔇 Ses kapalı' : '🔊 Ses açık');
    button.on('pointerdown', toggle); label.setInteractive({ useHandCursor: true }).on('pointerdown', toggle);
  }

  update(_time: number, delta: number) {
    this.simulate(Math.min(delta, 80));
  }

  private get activeQuestion() { return this.config.questions[this.questionIndex]; }

  private simulate(deltaMs: number) {
    const raceIsMoving = this.phase !== 'vehicle-select' && this.phase !== 'finish';
    if (raceIsMoving) {
      this.motionState = advanceRaceMotion(this.motionState, deltaMs);
      this.cameraDistance += RACE_CRUISE_SPEED * deltaMs / 1_000;
      const cameraCatchup = Math.min(0.14, deltaMs * 0.0008);
      this.cameraDistance += (this.motionState.racers.player.distance - this.cameraDistance) * cameraCatchup;
    } else if (this.phase === 'vehicle-select') {
      this.cameraDistance += deltaMs * 0.018;
    }
    if (this.lanePulse) {
      this.lanePulse.elapsedMs = Math.min(this.lanePulse.durationMs, this.lanePulse.elapsedMs + deltaMs);
      if (this.lanePulse.elapsedMs >= this.lanePulse.durationMs) this.lanePulse = undefined;
    }
    this.drawWorld();
    this.animateCars(deltaMs);
    const playerBoost = this.motionState.racers.player.boostRemainingMs > 0;
    this.audio.setEngineLoad(playerBoost ? 1 : 0.52 + Math.sin(this.cameraDistance / 420) * 0.12);
    if (this.phase === 'feedback') this.refreshHud();
    if (this.phase !== 'racing') return;
    this.questionElapsedMs += deltaMs;
    if (this.remainingMs == null) return;
    this.remainingMs = Math.max(0, this.remainingMs - deltaMs);
    this.refreshHud();
    if (this.remainingMs === 0) this.resolveAnswer('timeout', null);
  }

  private drawWorld() {
    const graphics = this.world.clear();
    const officialProgress = Math.min(1, this.ruleState.answered / Math.max(1, this.config.questions.length));
    const sector = sectorForProgress(officialProgress);
    const skies: Record<string, [number, number, number]> = {
      forest: [0x79c9e8, 0xffd685, 0x315d55], mountain: [0x72add1, 0xf3bd79, 0x695b61],
      tunnel: [0x293249, 0x65496d, 0x222834], sunset: [0xf47c69, 0xffc36d, 0x5d365f], finish: [0x5d3f83, 0xffb35c, 0x24344d],
    };
    const [top, horizon, ground] = skies[sector];
    graphics.fillStyle(top).fillRect(0, 0, WIDTH, 150);
    graphics.fillStyle(horizon).fillRect(0, 150, WIDTH, 150);
    graphics.fillStyle(ground).fillRect(0, 300, WIDTH, HEIGHT - 300);
    const parallax = (this.cameraDistance * 0.13) % 320;
    graphics.fillStyle(0x5b5572, 0.75);
    for (let x = -360 - parallax; x < WIDTH + 360; x += 260) {
      graphics.fillTriangle(x, 300, x + 150, 115 + ((x / 260) % 2) * 35, x + 320, 300);
    }
    graphics.fillStyle(sector === 'forest' ? 0x285b47 : 0x4a4d59, 0.9);
    for (let x = -80 - ((this.cameraDistance * 0.22) % 120); x < WIDTH + 100; x += 92) {
      graphics.fillTriangle(x, 325, x + 42, 218, x + 84, 325);
      graphics.fillRect(x + 38, 315, 9, 38);
    }
    if (sector === 'tunnel') {
      graphics.fillStyle(0x131a25, 0.82).fillRect(0, 0, WIDTH, 286);
      for (let x = -80 - ((this.cameraDistance * 0.31) % 190); x < WIDTH + 190; x += 190) {
        graphics.fillStyle(0xffd676, 0.7).fillRoundedRect(x, 232, 72, 8, 4);
      }
    }
    // Curved perspective road. Every road edge and lane marker is derived from
    // the same world-distance projection used by the cars.
    const samples = Array.from({ length: 25 }, (_, index) => {
      const y = 250 + index * 20;
      const relativeDistance = (612 - y) / 2.05;
      const center = roadCenterX(this.cameraDistance + relativeDistance);
      const half = roadHalfWidth(y);
      return { y, center, half };
    });
    const roadPolygon = [
      ...samples.map((point) => new PhaserMath.Vector2(point.center - point.half, point.y)),
      ...samples.slice().reverse().map((point) => new PhaserMath.Vector2(point.center + point.half, point.y)),
    ];
    graphics.fillStyle(0x18222d).fillPoints(roadPolygon, true);
    graphics.lineStyle(13, 0xe8b455, 1);
    graphics.beginPath(); samples.forEach((point, index) => index === 0
      ? graphics.moveTo(point.center - point.half, point.y)
      : graphics.lineTo(point.center - point.half, point.y)); graphics.strokePath();
    graphics.beginPath(); samples.forEach((point, index) => index === 0
      ? graphics.moveTo(point.center + point.half, point.y)
      : graphics.lineTo(point.center + point.half, point.y)); graphics.strokePath();
    for (let index = 0; index < 14; index += 1) {
      const phase = ((index * 92 - this.cameraDistance * 0.72) % 1_288 + 1_288) % 1_288 / 1_288;
      const y = 255 + Math.pow(phase, 2) * 510;
      const relativeDistance = (612 - y) / 2.05;
      const center = roadCenterX(this.cameraDistance + relativeDistance);
      const width = 3 + phase * 18;
      const height = 7 + phase * 38;
      graphics.fillStyle(0xf7e8a8, 0.94).fillRoundedRect(center - width / 2, y, width, height, 3);
    }
    // Real roadside objects cross the camera instead of leaving only the lane
    // stripe moving. Objects are deterministic world segments and are reused.
    for (let index = 0; index < 18; index += 1) {
      const spacing = 82;
      const relative = ((index * spacing - this.cameraDistance) % (spacing * 18) + spacing * 18) % (spacing * 18);
      if (relative > 720) continue;
      const depth = 1 - relative / 720;
      const y = 255 + Math.pow(depth, 1.75) * 525;
      const center = roadCenterX(this.cameraDistance + relative);
      const half = roadHalfWidth(y);
      const side = index % 2 === 0 ? -1 : 1;
      const x = center + side * (half + 18 + depth * 54);
      const size = 4 + depth * 43;
      if ((index + sector.length) % 3 === 0) {
        graphics.fillStyle(0x193d32, 0.98).fillRect(x - size * 0.08, y - size * 0.15, size * 0.16, size * 0.55);
        graphics.fillStyle(sector === 'sunset' ? 0x8c5a3f : 0x2a7957, 0.98).fillCircle(x, y - size * 0.45, size * 0.52);
      } else {
        graphics.fillStyle(index % 4 ? 0xece8d7 : 0x5de2ef, 0.94).fillRoundedRect(x - size * 0.12, y - size * 0.65, size * 0.24, size * 0.72, 3);
        graphics.fillStyle(0x172832, 0.96).fillRect(x - size * 0.35, y, size * 0.7, size * 0.13);
      }
    }
    if (sector === 'finish' && officialProgress > 0.9) {
      const y = 286 + Math.max(0, officialProgress - 0.9) * 2500;
      const tile = 30;
      for (let x = 350; x < 930; x += tile) {
        graphics.fillStyle(((x / tile) % 2) ? 0xffffff : 0x111111).fillRect(x, y, tile, 18);
        graphics.fillStyle(((x / tile) % 2) ? 0x111111 : 0xffffff).fillRect(x, y + 18, tile, 18);
      }
    }
    // Moving particles, lights and flags make the road feel alive.
    for (let index = 0; index < 14; index += 1) {
      const x = (index * 137 + this.cameraDistance * (0.05 + index * 0.002)) % WIDTH;
      const y = 90 + ((index * 73) % 180);
      graphics.fillStyle(index % 3 === 0 ? 0xffffff : 0xffe3a1, 0.28).fillCircle(x, y, 2 + index % 3);
    }
  }

  private createPickerCar(color: TurboVehicleColor, x: number, y: number, scale: number, depth: number) {
    return this.add.image(x, y, CAR_TEXTURE, colors.indexOf(color))
      .setScale(scale).setOrigin(0.5).setDepth(depth);
  }

  private showVehiclePicker() {
    const shade = this.add.rectangle(WIDTH / 2, HEIGHT / 2, WIDTH, HEIGHT, 0x071322, 0.88).setDepth(80);
    const panel = this.add.rectangle(WIDTH / 2, HEIGHT / 2 + 8, 1080, 530, 0x0c2638, 0.97)
      .setStrokeStyle(4, 0x55dded).setDepth(81);
    const title = this.add.text(WIDTH / 2, 128, 'YARIŞ ARACINI SEÇ', {
      fontFamily: 'system-ui, sans-serif', fontSize: '42px', fontStyle: 'bold', color: '#ffe071',
    }).setOrigin(0.5).setDepth(82);
    const copy = this.add.text(WIDTH / 2, 180, 'Üç araç aynı güce sahip. Tarzını seç ve Turbo Düellosu’na başla!', {
      fontFamily: 'system-ui, sans-serif', fontSize: '21px', color: '#d8f6ff',
    }).setOrigin(0.5).setDepth(82);
    this.overlayObjects = [shade, panel, title, copy];
    const labels = ['KORAL ŞİMŞEK', 'MAVİ VOLT', 'YEŞİL FIRTINA'];
    colors.forEach((color, index) => {
      const x = 350 + index * 290;
      const card = this.add.rectangle(x, 405, 250, 300, index === 0 ? 0x4e2730 : index === 1 ? 0x15395d : 0x29462d, 0.95)
        .setStrokeStyle(3, index === 0 ? 0xff775d : index === 1 ? 0x50bfff : 0xa8f04f)
        .setInteractive({ useHandCursor: true }).setDepth(82);
      const car = this.createPickerCar(color, x, 397, 0.31, 83);
      const label = this.add.text(x, 518, labels[index], {
        fontFamily: 'system-ui, sans-serif', fontSize: '19px', fontStyle: 'bold', color: '#ffffff',
      }).setOrigin(0.5).setDepth(84);
      const choose = () => this.selectVehicle(color);
      card.on('pointerdown', choose); car.setInteractive({ useHandCursor: true }).on('pointerdown', choose); label.setInteractive({ useHandCursor: true }).on('pointerdown', choose);
      this.overlayObjects.push(card, car, label);
    });
  }

  private selectVehicle(color: TurboVehicleColor) {
    if (this.phase !== 'vehicle-select') return;
    this.selectedColor = color;
    this.overlayObjects.forEach((object) => object.destroy());
    this.overlayObjects = [];
    this.createRaceCars();
    this.phase = 'countdown';
    this.audio.play('select');
    this.audio.startEngine();
    const countdown = this.add.text(WIDTH / 2, HEIGHT / 2 - 30, '3', {
      fontFamily: 'system-ui, sans-serif', fontSize: '118px', fontStyle: 'bold', color: '#ffe36a', stroke: '#102633', strokeThickness: 12,
    }).setOrigin(0.5).setDepth(70);
    ['2', '1', 'YARIŞ!'].forEach((label, index) => this.time.delayedCall((index + 1) * 650, () => {
      countdown.setText(label).setScale(1.25).setAlpha(0);
      this.tweens.add({ targets: countdown, alpha: 1, scale: 1, duration: 220 });
      if (label === 'YARIŞ!') this.audio.play('start');
    }));
    this.time.delayedCall(2_650, () => {
      countdown.destroy();
      this.startQuestion();
    });
    this.refreshHud();
  }

  private createRaceCars() {
    const rivals = colors.filter((color) => color !== this.selectedColor);
    const accents: Record<TurboVehicleColor, number> = { coral: 0xff8a38, azure: 0x49e8ff, lime: 0xb7ff48 };
    const makeRig = (color: TurboVehicleColor) => new RaceVehicleRig(this, {
      texture: RACE_CAR_TEXTURE, frame: colors.indexOf(color), accent: accents[color],
    });
    this.playerCar = makeRig(this.selectedColor);
    this.botCars = [
      makeRig(rivals[0]),
      makeRig(rivals[1]),
    ];
    this.snapRaceCars();
  }

  private visualLane(baseLane: number, actor: 'player' | 'botOne' | 'botTwo') {
    if (!this.lanePulse) return baseLane;
    const progress = this.lanePulse.elapsedMs / this.lanePulse.durationMs;
    const swing = Math.sin(progress * Math.PI);
    if (this.lanePulse.outcome === 'correct' && actor === 'player') return baseLane + swing * 0.82 * this.lanePulse.direction;
    if (this.lanePulse.outcome !== 'correct' && actor !== 'player') {
      const direction = actor === 'botOne' ? -1 : 1;
      return baseLane - direction * swing * 0.34;
    }
    return baseLane;
  }

  private snapRaceCars() {
    if (!this.playerCar) return;
    const entries = [
      { rig: this.playerCar, racer: this.motionState.racers.player },
      { rig: this.botCars[0], racer: this.motionState.racers.botOne },
      { rig: this.botCars[1], racer: this.motionState.racers.botTwo },
    ];
    entries.forEach(({ rig, racer }) => {
      if (!rig) return;
      const point = projectRoadPoint({
        cameraDistance: this.cameraDistance,
        relativeDistance: racer.distance - this.cameraDistance,
        lane: racer.lane,
      });
      rig.container.setPosition(point.x, point.y).setScale(point.scale).setRotation(point.rotation).setDepth(Math.round(point.y));
    });
  }

  private animateCars(deltaMs: number) {
    if (!this.playerCar) return;
    const entries = [
      { actor: 'player' as const, rig: this.playerCar, racer: this.motionState.racers.player },
      { actor: 'botOne' as const, rig: this.botCars[0], racer: this.motionState.racers.botOne },
      { actor: 'botTwo' as const, rig: this.botCars[1], racer: this.motionState.racers.botTwo },
    ];
    entries.forEach(({ actor, rig, racer }) => {
      if (!rig) return;
      const point = projectRoadPoint({
        cameraDistance: this.cameraDistance,
        relativeDistance: racer.distance - this.cameraDistance,
        lane: this.visualLane(racer.lane, actor),
      });
      const turboStrength = racer.boostRemainingMs <= 0 ? 0 : Math.min(1, racer.boostRemainingMs / 300);
      rig.setRoadTransform(point, racerSpeed(racer), deltaMs, turboStrength);
    });
  }

  private startQuestion() {
    this.clearQuestion();
    this.phase = 'racing';
    this.questionElapsedMs = 0;
    this.remainingMs = this.activeQuestion.timeLimitSeconds == null ? null : this.activeQuestion.timeLimitSeconds * 1000;
    this.renderQuestion();
    this.refreshHud();
  }

  private renderQuestion() {
    const question = this.activeQuestion;
    const panel = this.add.rectangle(WIDTH / 2, 73, 1110, 112, 0x09243a, 0.96).setStrokeStyle(3, 0x55dcea).setDepth(40);
    this.promptObjects.push(panel);
    let promptX = WIDTH / 2;
    let promptWidth = 990;
    if (question.promptMediaUrl) {
      const key = this.mediaTextureKeys.get(question.promptMediaUrl);
      if (key) {
        const media = this.add.image(155, 73, key).setDisplaySize(116, 82).setDepth(42);
        promptX = 690; promptWidth = 850; this.promptObjects.push(media);
      }
    }
    const prompt = this.add.text(promptX, 73, question.prompt || 'Görseldeki soruyu cevapla', {
      fontFamily: 'system-ui, sans-serif', fontSize: question.prompt.length > 100 ? '22px' : question.prompt.length > 60 ? '26px' : '31px',
      fontStyle: 'bold', color: '#ffffff', align: 'center', wordWrap: { width: promptWidth, useAdvancedWrap: true },
    }).setOrigin(0.5).setDepth(42);
    this.promptObjects.push(prompt);

    const count = question.choices.length;
    const gap = 14;
    const width = Math.min(230, (1160 - gap * (count - 1)) / count);
    const startX = WIDTH / 2 - ((width + gap) * count - gap) / 2 + width / 2;
    question.choices.forEach((choice, index) => {
      const x = startX + index * (width + gap);
      const container = this.add.container(x, 194).setDepth(43);
      const background = this.add.graphics();
      background.fillStyle(index % 2 ? 0x263f68 : 0x154966, 0.98).fillRoundedRect(-width / 2, -52, width, 104, 18);
      background.lineStyle(3, index % 2 ? 0xb08bff : 0x5ce8ef).strokeRoundedRect(-width / 2, -52, width, 104, 18);
      container.add(background);
      let textX = 0;
      let textWidth = width - 24;
      if (choice.mediaUrl) {
        const key = this.mediaTextureKeys.get(choice.mediaUrl);
        if (key) {
          const image = this.add.image(choice.label ? -width * 0.28 : 0, 0, key);
          const size = choice.label ? 67 : 92;
          image.setDisplaySize(size, size).setOrigin(0.5);
          container.add(image);
          if (choice.label) { textX = width * 0.18; textWidth = width * 0.5; }
        }
      }
      if (choice.label) {
        const label = this.add.text(textX, 0, `${index + 1}. ${choice.label}`, {
          fontFamily: 'system-ui, sans-serif', fontSize: choice.label.length > 50 ? '14px' : choice.label.length > 28 ? '16px' : '19px',
          fontStyle: 'bold', color: '#ffffff', align: 'center', wordWrap: { width: textWidth, useAdvancedWrap: true },
        }).setOrigin(0.5);
        container.add(label);
      }
      container.setSize(width, 104).setInteractive({ useHandCursor: true }).on('pointerdown', () => this.choose(index));
      container.on('pointerover', () => this.tweens.add({ targets: container, scale: 1.045, duration: 100 }));
      container.on('pointerout', () => this.tweens.add({ targets: container, scale: 1, duration: 100 }));
      this.answerActors.push({ container, choice, index });
    });
  }

  private choose(index: number) {
    if (this.phase !== 'racing') return;
    const choice = this.answerActors[index];
    if (!choice) return;
    this.audio.play('select');
    this.resolveAnswer(choice.choice.correct ? 'correct' : 'wrong', index);
  }

  private resolveAnswer(outcome: RaceAnswerOutcome, selectedChoiceIndex: number | null) {
    if (this.phase !== 'racing') return;
    this.phase = 'feedback';
    const responseMs = Math.round(this.questionElapsedMs);
    const correctChoiceIndex = this.activeQuestion.choices.findIndex((choice) => choice.correct);
    const beforePlace = raceMotionPlacement(this.motionState).place;
    const evaluated = applyRaceAnswer(this.ruleState, {
      outcome, responseMs, timeLimitMs: this.activeQuestion.timeLimitSeconds == null ? null : this.activeQuestion.timeLimitSeconds * 1000,
    });
    this.ruleState = evaluated.state;
    this.motionState = applyRaceMotionOutcome(this.motionState, outcome);
    this.lanePulse = {
      outcome, elapsedMs: 0, durationMs: RACE_BOOST_DURATION_MS,
      direction: this.questionIndex % 2 === 0 ? -1 : 1,
    };
    const placement = raceMotionPlacement(advanceRaceMotion(this.motionState, RACE_BOOST_DURATION_MS));
    this.questionResults.push({
      level: this.questionIndex + 1, durationMs: responseMs, outcome, selectedChoiceIndex, correctChoiceIndex,
      baseScore: evaluated.breakdown.base, speedBonus: evaluated.breakdown.speed, streakBonus: evaluated.breakdown.streak,
      scoreDelta: evaluated.scoreDelta, placeAfterQuestion: placement.place, botGaps: placement.gaps,
    });
    if (outcome === 'correct') {
      this.audio.play('correct'); this.audio.play('turbo'); this.playTurbo();
      if (placement.place < beforePlace) this.audio.play('overtake');
    } else {
      this.audio.play(outcome === 'timeout' ? 'timeout' : 'wrong');
      this.cameras.main.shake(260, 0.009);
    }
    this.showFeedback(outcome, correctChoiceIndex, evaluated.scoreDelta);
    this.refreshHud();
    this.time.delayedCall(1_350, () => {
      if (this.questionIndex >= this.config.questions.length - 1) this.finishRace();
      else { this.questionIndex += 1; this.startQuestion(); }
    });
  }

  private showFeedback(outcome: RaceAnswerOutcome, correctIndex: number, delta: number) {
    this.answerActors.forEach((actor) => {
      actor.container.disableInteractive();
      if (actor.index !== correctIndex) actor.container.setAlpha(0.42);
      else this.tweens.add({ targets: actor.container, scale: 1.09, duration: 180, yoyo: true, repeat: 1 });
    });
    const label = outcome === 'correct'
      ? `TURBO!  +${delta}`
      : outcome === 'timeout'
        ? `SÜRE DOLDU  ${delta}`
        : `YANLIŞ  ${delta} · DOĞRU CEVAP GÖSTERİLDİ`;
    this.feedbackText = this.add.text(WIDTH / 2, 290, label, {
      fontFamily: 'system-ui, sans-serif', fontSize: outcome === 'correct' ? '42px' : '30px', fontStyle: 'bold',
      color: outcome === 'correct' ? '#ffe66b' : '#ff8b7d', stroke: '#102633', strokeThickness: 8,
    }).setOrigin(0.5).setDepth(55);
  }

  private playTurbo() {
    if (!this.playerCar || !this.speedLines) return;
    this.tweens.add({ targets: this.playerCar.container, scaleX: this.playerCar.container.scaleX * 1.08, scaleY: this.playerCar.container.scaleY * 1.08, duration: 210, yoyo: true, ease: 'Sine.inOut' });
    const lines = this.speedLines.clear();
    lines.lineStyle(5, 0x70f7ff, 0.8);
    for (let index = 0; index < 22; index += 1) {
      const x = PhaserMath.Between(20, WIDTH - 20); const y = PhaserMath.Between(310, 690);
      lines.beginPath().moveTo(x, y).lineTo(x + (x < WIDTH / 2 ? -90 : 90), y + 80).strokePath();
    }
    this.tweens.add({ targets: lines, alpha: 0, duration: 650, onComplete: () => lines.clear().setAlpha(1) });
  }

  private finishRace() {
    this.clearQuestion();
    this.phase = 'finish';
    this.audio.play('finish');
    const placement = raceMotionPlacement(this.motionState);
    this.cameras.main.flash(500, 255, 238, 150);
    this.time.delayedCall(500, () => this.showFinishPanel(placement.place));
    const result: FinishPlayAttemptInput = {
      score: this.ruleState.score,
      lives: 3,
      correct: this.ruleState.correct,
      wrong: this.ruleState.wrong + this.ruleState.timeouts,
      attempts: this.ruleState.answered,
      completed: true,
      details: {
        templateKey: 'turbo-duel', vehicleColor: this.selectedColor, finalPlace: placement.place,
        maxStreak: this.ruleState.maxStreak, turboCount: this.ruleState.turboCount, questions: this.questionResults,
      },
    };
    if (!this.resultEmitted) {
      this.resultEmitted = true;
      EventBus.emit(RACE_GAME_FINISHED_EVENT, { gameId: this.gameId, result } satisfies RaceGameResult);
    }
    this.refreshHud();
  }

  private showFinishPanel(place: 1 | 2 | 3) {
    const fastest = this.questionResults.filter((question) => question.outcome === 'correct').reduce((best, question) => Math.min(best, question.durationMs), Infinity);
    const reviewItems = buildLearningReview(this.config.questions.map((question) => ({
      prompt: question.prompt,
      promptMediaUrl: question.promptMediaUrl,
      choices: question.choices.map((choice) => ({ text: choice.label, mediaUrl: choice.mediaUrl, correct: choice.correct })),
    })), this.questionResults.map((result, index) => ({
      label: `${index + 1}. yarış sorusu`,
      questionIndex: index,
      wrongChoiceIndices: result.outcome === 'wrong' && result.selectedChoiceIndex != null
        ? [result.selectedChoiceIndex]
        : [],
      wrongCount: result.outcome === 'wrong' ? 1 : 0,
      timeoutCount: result.outcome === 'timeout' ? 1 : 0,
      completed: true,
      completionStatus: result.outcome === 'correct' ? undefined : 'answer-shown',
    })));

    for (let index = 0; index < 80; index += 1) {
      const confetti = this.add.rectangle(PhaserMath.Between(180, 1100), PhaserMath.Between(-120, 100), 8, 16,
        [0xffd84d, 0x57e1f1, 0xff6c65, 0xa5ed62][index % 4]).setDepth(RESULT_OVERLAY_DEPTH - 1).setRotation(Math.random() * Math.PI);
      this.tweens.add({ targets: confetti, y: HEIGHT + 30, rotation: confetti.rotation + 8, duration: PhaserMath.Between(1800, 3600), delay: index * 18 });
      this.overlayObjects.push(confetti);
    }
    const overlay = createLearningResultOverlay(this, {
      centerX: WIDTH / 2,
      centerY: HEIGHT / 2,
      viewportWidth: WIDTH,
      viewportHeight: HEIGHT,
      title: place === 1 ? 'ŞAMPİYON!' : `${place}. SIRADA BİTİRDİN!`,
      titleColor: '#ffe36b',
      accentColor: 0xffd65c,
      summaryLines: [
        `${this.ruleState.score} puan · ${this.ruleState.correct}/${this.config.questions.length} doğru`,
        `${this.ruleState.wrong} yanlış · ${this.ruleState.timeouts} süre aşımı · en uzun seri ${this.ruleState.maxStreak}`,
        `${this.ruleState.turboCount} turbo · en hızlı doğru ${Number.isFinite(fastest) ? `${(fastest / 1000).toFixed(1)} sn` : '—'}`,
      ],
      reviewItems,
      restartEnabled: this.restartEnabled,
      restartLabel: 'Yeniden yarış',
      onRestart: () => this.scene.restart(),
      resolveTextureKey: (mediaUrl) => this.mediaTextureKeys.get(mediaUrl),
    });
    overlay.setAlpha(0);
    this.tweens.add({ targets: overlay, alpha: 1, duration: 260, ease: 'Sine.out' });
    this.overlayObjects.push(overlay);
  }

  private clearQuestion() {
    this.promptObjects.forEach((object) => object.destroy());
    this.answerActors.forEach((actor) => actor.container.destroy());
    this.feedbackText?.destroy();
    this.promptObjects = []; this.answerActors = []; this.feedbackText = undefined;
  }

  private refreshHud() {
    const place = raceMotionPlacement(this.motionState).place;
    EventBus.emit(RACE_HUD_UPDATE_EVENT, {
      gameId: this.gameId,
      hud: {
        question: Math.min(this.questionIndex + 1, this.config.questions.length), totalQuestions: this.config.questions.length,
        phase: this.phase, score: this.ruleState.score, place, streak: this.ruleState.streak, remainingMs: this.remainingMs,
      },
    } satisfies RaceHudUpdate);
  }

  private installDebugHooks() {
    window.render_game_to_text = () => {
      const placement = raceMotionPlacement(this.motionState);
      const trackProgress = Math.min(1, this.ruleState.answered / Math.max(1, this.config.questions.length));
      return JSON.stringify({
        template: 'turbo-duel', phase: this.phase, question: this.questionIndex + 1, totalQuestions: this.config.questions.length,
        trackProgress, sector: sectorForProgress(trackProgress), selectedColor: this.selectedColor,
        score: this.ruleState.score, streak: this.ruleState.streak, correct: this.ruleState.correct,
        wrong: this.ruleState.wrong, timeouts: this.ruleState.timeouts, remainingMs: this.remainingMs,
        place: placement.place, botGaps: placement.gaps,
        raceDistances: {
          player: Math.round(this.motionState.racers.player.distance),
          botOne: Math.round(this.motionState.racers.botOne.distance),
          botTwo: Math.round(this.motionState.racers.botTwo.distance),
        },
        options: this.activeQuestion?.choices.map((choice, index) => ({ index: index + 1, text: choice.label, correct: choice.correct })),
      });
    };
    window.advanceTime = (milliseconds: number) => {
      let remaining = Math.max(0, milliseconds);
      while (remaining > 0) { const step = Math.min(50, remaining); this.simulate(step); remaining -= step; }
    };
  }
}
